import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  Alert,
  Divider,
  useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useUser } from 'app/context/UserContext';

interface PasswordResetSectionProps {
  userId: string;
}

const PasswordResetSection: React.FC<PasswordResetSectionProps> = ({ userId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [resetPassword, setResetPassword] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const { resetUserPassword } = useUser();
  const theme = useTheme();

  // Effect to handle countdown and password clearing
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resetPassword) {
      setTimeLeft(15); // Reset timer when new password is generated
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setResetPassword(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resetPassword]);

  // Effect to clear copied state after 2 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (copied) {
      timer = setTimeout(() => {
        setCopied(false);
      }, 2000); // 2 seconds
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [copied]);

  // Mock function to generate a random password
  // const generateRandomPassword = () => {
  //   const length = 12;
  //   const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  //   let password = "";
  //   for (let i = 0; i < length; i++) {
  //     const randomIndex = Math.floor(Math.random() * charset.length);
  //     password += charset[randomIndex];
  //   }
  //   return password;
  // };

  const handleResetPassword = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await resetUserPassword(userId);
      
      if (response.errorCode) {
        throw new Error(response.message || 'Failed to reset password');
      }

      setResetPassword(response.body.newPassword
      );
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyPassword = () => {
    if (resetPassword) {
      navigator.clipboard.writeText(resetPassword);
      setCopied(true);
    }
  };

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
        Password Management
      </Typography>

      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {resetPassword ? (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              mb: 1
            }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                New Password: {resetPassword}
              </Typography>
              <Tooltip 
                title={copied ? "Copied!" : "Copy Password"}
                arrow
                placement="top"
              >
                <IconButton 
                  size="small" 
                  onClick={handleCopyPassword}
                  sx={{ 
                    color: copied ? 'success.main' : 'primary.main',
                    transition: 'color 0.2s ease-in-out'
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Alert 
              severity="warning" 
              sx={{ 
                mt: 1,
                '& .MuiAlert-message': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }
              }}
            >
              <Typography variant="body2">
                Please copy the password now. It will disappear in
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600,
                  color: 'warning.dark',
                  minWidth: '20px',
                  textAlign: 'center'
                }}
              >
                {timeLeft}
              </Typography>
              <Typography variant="body2">
                seconds
              </Typography>
            </Alert>
          </Box>
        ) : (
          <Button
            variant="contained"
            onClick={handleResetPassword}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: theme.palette.secondary.main,
              color: 'white',
              py: 0.75,
              px: 3,
              '&:hover': {
                bgcolor: '#0a1f3a',
              }
            }}
          >
            {isLoading ? 'Resetting Password...' : 'Reset Password'}
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default PasswordResetSection; 